import React, { useEffect, useState } from 'react'
import {
  FormDate,
  PickerModal,
  RecurrenceForm,
  Screen,
  Text,
  TextInput,
  TimepickerModal,
  Icon as CustomIcon,
  FormLocation,
} from '@components'
import { ScrollView, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Button } from '@components'
import { Theme } from '@theme'
import { StyleSheet } from 'react-native'
import dayjs, { fromApiTime } from '../../../utils/dayjs'
import { getDefaultKeepStart } from './keepFormDefaults'
import { useForm } from 'react-hook-form'
import {
  useCreateKeepContact,
  useCreationStackIntegration,
  useGetKeepContactById,
  useScreen,
  useUpdateKeepContact,
} from '@hooks'
import { useSafeNavigation, useSafeRoute } from '../../../navigation/web/useSafeNavigation'
import DatePicker from 'react-native-date-picker'
import ContactsListEdit from '../components/ContactsListEdit'
import { ContactChip } from './components/ContactChip'
import { EncounterPlace, getCommonStyles } from '@utils'
import { useLanguage } from '@context'

const AddKeep: React.FC = () => {
  const theme = useTheme()
  const { t } = useLanguage()
  const { isMobile, isWeb } = useScreen()
  const styles = getStyle(theme)
  const commonStyles = getCommonStyles(theme)
  const navigation = useSafeNavigation()
  const route: any = useSafeRoute()

  const { currentStack, hideCreationStack, isVisible } = useCreationStackIntegration()

  // Use prop keepContact or default to null
  const keepContactId = route?.params?.keepContactId || currentStack?.data?.keepContactId || ''
  const contactId = route?.params?.contactId || currentStack?.data?.contactId
  const isEdit = !!keepContactId

  const { data: keepContact } = useGetKeepContactById(keepContactId)

  const [name, setName] = useState('')
  // One moment for both fields: the day follows the hour past midnight.
  const [defaultStart] = useState(() => getDefaultKeepStart())
  const [date, setDate] = useState(defaultStart.toDate())
  const [time, setTime] = useState(defaultStart.format('HH:mm'))
  const [description, setDescription] = useState('')
  const [rrule, setRrule] = useState<string | null>(null)
  const [location, setLocation] = useState<EncounterPlace>({
    place: '',
    city: '',
    country: '',
    state: '',
    region: '',
  })
  // A new follow-up rings the phone on its day unless the alert is switched off.
  const [hasReminder, setHasReminder] = useState(true)

  const [contactIds, setContactIds] = useState<string[]>([])
  const [isTimepickerVisible, setIsTimepickerVisible] = useState(false)
  const [isDatepickerVisible, setIsDatepickerVisible] = useState(false)
  const [isAlertPicker, setIsAlertPicker] = useState(false)
  const [isInvitedPeopleModalVisible, setIsInvitedPeopleModalVisible] = useState(false)

  const { mutate: createKeepContact, isPending: isCreating } = useCreateKeepContact()
  const { mutate: updateKeepContact, isPending: isUpdating } = useUpdateKeepContact()
  const isSaving = isCreating || isUpdating

  const { control } = useForm<any>()

  useEffect(() => {
    if (keepContact) {
      setName(keepContact.name)
      setDate(keepContact.keepDate ? dayjs(keepContact.keepDate).toDate() : dayjs().toDate())
      setTime(keepContact.time ? fromApiTime(keepContact.time) : '12:00')
      setDescription(keepContact.description || '')
      setRrule(keepContact.rrule ?? null)
      setLocation(
        keepContact.location ?? { place: '', city: '', country: '', state: '', region: '' },
      )
      setHasReminder(keepContact.hasReminder ?? false)
    }
  }, [keepContact])

  useEffect(() => {
    setContactIds(
      keepContact?.contactAccountId
        ? [keepContact?.contactAccountId]
        : contactId
          ? [contactId]
          : [],
    )
  }, [keepContact, contactId])

  const handleBack = () => {
    if (isVisible) {
      hideCreationStack()
    } else {
      navigation?.goBack()
    }
  }

  const handleSave = () => {
    if (!name || contactIds.length === 0 || isSaving) return

    const keepDate = dayjs(date)
      .hour(parseInt(time.split(':')[0], 10))
      .minute(parseInt(time.split(':')[1], 10))
      .toISOString()
    const data = {
      name,
      description,
      contactAccountId: contactIds[0],
      date: keepDate,
      rrule: rrule ?? null,
      location: location?.place ? location : null,
      hasReminder,
    }

    // The form closes once the server has accepted the save: a refused one
    // leaves it open with what was typed.
    if (isEdit && keepContact?.id) {
      updateKeepContact({ id: keepContact.id, ...data }, { onSuccess: handleBack })
    } else {
      createKeepContact(data, { onSuccess: handleBack })
    }
  }

  return (
    <Screen>
      <View style={[styles.header, isWeb && commonStyles.paddingTop]}>
        {isMobile ? (
          <TouchableOpacity onPress={handleBack} testID="keep-back-button">
            <CustomIcon name="arrowBack" size={25} />
          </TouchableOpacity>
        ) : (
          <View style={styles.placeholder} />
        )}
        <Text type="titleBold">
          {isEdit ? t('keepContact.editReminder') : t('keepContact.createReminder')}
        </Text>
        <View style={styles.placeholder} />
      </View>
      {/* `automaticallyAdjustKeyboardInsets` (iOS) adds the keyboard overlap as a
            scroll inset AND scrolls the focused input above the keyboard — so the
            Description field and the validate CTA stay reachable. Android relies on
            the manifest's `adjustResize`. */}
      <ScrollView
        style={styles.scrollContent}
        contentContainerStyle={commonStyles.paddingBottom}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        automaticallyAdjustKeyboardInsets
      >
        {/* Input avec icône edit */}
        <TextInput
          testID="keep-title-input"
          title={t('keepContact.reminderTitle')}
          placeholder={t('keepContact.reminderTitlePlaceholder')}
          value={name}
          onChangeText={setName}
          containerStyle={styles.inputContainer}
        />
        {/*<Text type="bodySmall">Type de rappel</Text>
          <View style={styles.formContainer}>
            <Switch 
              left={{label: "Privée", icon: 'lock', color: theme.colors.green, value: false}}
              right={{label: "Pro", icon: 'work', color: theme.colors.primary, value: true}}
              value={scope === Scope.PROFESSIONAL}
              onChange={(value) => setScope(value? Scope.PROFESSIONAL: Scope.PRIVATE)}
            />
          </View>*/}
        {!contactId && (
          <TouchableOpacity onPress={() => setIsInvitedPeopleModalVisible(true)}>
            <TextInput
              containerStyle={styles.textInputContainer}
              style={styles.blackTextInput}
              title={t('keepContact.addContact')}
              iconRight="search"
              placeholder={t('keepContact.searchContact')}
              editable={false}
              pointerEvents="none"
            />
          </TouchableOpacity>
        )}
        {!!contactId && (
          <Text type="bodySmall" color={theme.colors.lightGrey}>
            {t('keepContact.contactLabel')}
          </Text>
        )}
        {contactIds.length > 0 && (
          <View style={styles.invitedContainer}>
            {contactIds.map((id) => (
              <ContactChip
                key={id}
                contactId={id}
                readOnly={!!contactId}
                onRemove={() => {
                  if (contactIds.length >= 1) {
                    setContactIds((prev) => prev.filter((id) => id !== id))
                  }
                }}
              />
            ))}
          </View>
        )}
        {isInvitedPeopleModalVisible && (
          <ContactsListEdit
            selectedContactIds={contactIds}
            setSelectedContactIds={setContactIds}
            fitParent={!isMobile}
            onClose={() => setIsInvitedPeopleModalVisible(false)}
          />
        )}
        <View style={styles.formContainerWithBottom}>
          <Text type="bodySmall" color={theme.colors.greyBlue}>
            {t('keepContact.eventDate')}
          </Text>
          <FormDate
            control={control}
            name="date"
            dateFormat="dddd DD MMMM YYYY"
            value={dayjs(date).format('DD-MM-YYYY')}
            onChange={(value) => {
              if (!isWeb) {
                setTime(dayjs(value).format('HH:mm'))
              }
            }}
            hourValue={time}
            onDatePressed={() => setIsDatepickerVisible(true)}
            onHourPressed={() => setIsTimepickerVisible(true)}
          />
        </View>
        <View style={styles.formContainer}>
          <RecurrenceForm
            value={rrule}
            onChange={setRrule}
            dtstart={dayjs(date)
              .hour(parseInt(time.split(':')[0], 10))
              .minute(parseInt(time.split(':')[1], 10))
              .toDate()}
          />
        </View>
        <TextInput
          testID="keep-description-input"
          title={t('keepContact.description')}
          placeholder={t('keepContact.descriptionPlaceholder')}
          value={description}
          onChangeText={setDescription}
          containerStyle={styles.inputContainer}
          isMultiline
        />
        <View style={styles.formContainer}>
          <FormLocation
            value={location.place}
            onChange={(value) => setLocation(value)}
            placeholder={t('keepContact.addLocation')}
            title={t('keepContact.location')}
            iconRight="mapPin"
          />
        </View>
        <TouchableOpacity onPress={() => setIsAlertPicker(true)}>
          <TextInput
            containerStyle={styles.textInputContainer}
            style={styles.blackTextInput}
            title={t('keepContact.alert')}
            iconLeft="bell"
            iconRight="arrowDown"
            value={hasReminder ? t('keepContact.alertEnabled') : t('keepContact.noAlert')}
            editable={false}
            pointerEvents="none"
          />
        </TouchableOpacity>
        <View style={styles.modalFooter}>
          <Button
            disabled={!name || contactIds.length === 0 || isSaving}
            title={t('common.validate')}
            onPress={handleSave}
          />
        </View>
      </ScrollView>

      <PickerModal
        isVisible={isAlertPicker}
        setIsVisible={setIsAlertPicker}
        data={[
          { id: 'true', name: t('keepContact.alertActive') },
          { id: 'false', name: t('keepContact.alertInactive') },
        ]}
        value={
          hasReminder
            ? { id: 'true', name: t('keepContact.alertActive') }
            : { id: 'false', name: t('keepContact.alertInactive') }
        }
        onSelect={(value) => setHasReminder(value.id === 'true')}
        fitParent={!isMobile}
      />
      <DatePicker
        //@ts-ignore
        fitParent
        modal
        mode="date"
        date={date}
        open={isDatepickerVisible}
        onConfirm={(selectedDate) => {
          const offset = selectedDate.getTimezoneOffset() * 60000
          const localDate = new Date(selectedDate.getTime() - offset)

          setDate(localDate)
          setIsDatepickerVisible(false)
        }}
        onCancel={() => setIsDatepickerVisible(false)}
      />
      <TimepickerModal
        isVisible={isTimepickerVisible}
        setIsVisible={setIsTimepickerVisible}
        onChange={setTime}
        value={time}
        fitParent={!isMobile}
      />
    </Screen>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingBottom: theme.spacing.md,
      backgroundColor: theme.colors.white,
      borderBottomWidth: 0.5,
      borderBottomColor: theme.colors.lightGrey,
    },
    placeholder: {
      width: 25,
    },
    scrollContent: {
      flex: 1,
      padding: theme.spacing.lg,
    },
    inputContainer: {
      marginBottom: theme.spacing.lg,
    },
    textInputContainer: {
      paddingTop: theme.spacing.lg,
    },
    formContainer: {
      marginTop: theme.spacing.md,
      marginBottom: theme.spacing.lg,
    },
    formContainerWithBottom: {
      marginVertical: theme.spacing.md,
      gap: theme.spacing.md,
    },
    blackTextInput: {
      color: theme.colors.secondary,
    },
    modalFooter: {
      paddingVertical: theme.spacing.md,
    },
    invitedContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginTop: theme.spacing.md,
    },
  })

export default AddKeep
